import { ref, computed, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { membersApi } from '@/entities/Branch';
import type { EligibleUser } from '@/entities/Branch';

export function useEligibleUserSearch() {
  const selectedUser = ref<EligibleUser | null>(null);
  const searchTerm = ref('');
  const searchBy = ref<'name' | 'registrationNumber'>('name');
  const searchResults = ref<EligibleUser[]>([]);
  const initialResults = ref<EligibleUser[]>([]);
  const isSearching = ref(false);

  const searchResultOptions = computed(() =>
    searchResults.value.map((u) => ({
      value: String(u.id),
      label: u.registrationNumber ? `${u.name} — ${u.registrationNumber}` : u.name,
    }))
  );

  const memberSearchEmptyMessage = computed(() =>
    searchTerm.value.trim() ? 'Nenhum usuário encontrado.' : ''
  );

  const debouncedSearch = useDebounceFn(async (term: string) => {
    try {
      searchResults.value = await membersApi.getEligibleUsers(term, searchBy.value);
    } catch {
      searchResults.value = initialResults.value;
    } finally {
      isSearching.value = false;
    }
  }, 300);

  async function searchUsers(term: string) {
    searchTerm.value = term;
    if (!term.trim()) {
      searchResults.value = initialResults.value;
      return;
    }
    isSearching.value = true;
    debouncedSearch(term);
  }

  function selectUser(user: EligibleUser) {
    selectedUser.value = user;
    searchTerm.value = '';
    searchResults.value = [];
  }

  function clearUser() {
    selectedUser.value = null;
    searchTerm.value = '';
    searchResults.value = initialResults.value;
  }

  function selectUserById(id: string) {
    if (!id) {
      clearUser();
      return;
    }
    const user = searchResults.value.find((u) => String(u.id) === id);
    if (user) selectUser(user);
  }

  async function loadInitialUsers() {
    try {
      const results = await membersApi.getEligibleUsers();
      initialResults.value = results;
      if (!searchTerm.value.trim()) {
        searchResults.value = results;
      }
    } catch {
      initialResults.value = [];
    }
  }

  function reset() {
    selectedUser.value = null;
    searchTerm.value = '';
    searchBy.value = 'name';
    searchResults.value = initialResults.value;
    isSearching.value = false;
  }

  watch(searchBy, () => searchUsers(searchTerm.value));

  return {
    selectedUser,
    searchTerm,
    searchBy,
    searchResults,
    isSearching,
    searchResultOptions,
    memberSearchEmptyMessage,
    searchUsers,
    selectUser,
    clearUser,
    selectUserById,
    loadInitialUsers,
    reset,
  };
}
